/**
 * exportColumns.ts — derivación compartida de columnas de export desde `columnConfig`.
 *
 * PT-#61: EXPORTAR debe producir EXACTAMENTE las mismas columnas que el exportador de
 * Publicaciones (títulos = `displayName`, precios con formato currency, Descuento, segmento
 * real del catalog vía JOIN, sin `apariciones`/`tags`). El bug previo era que ExportPanel
 * armaba columnas iterando el schema FÍSICO (`DESCRIBE`) y buscando `columnConfig.core[<idFísico>]`,
 * pero las claves de `core` son LÓGICAS (`precio_venta`, `marca`, `categoria`), no físicas
 * (`sale_price`, `Marca`, `Cat`) → el lookup fallaba y se colaban headers crudos, se perdía el
 * formato de precio, y se exportaba `segment` (int físico) en vez de `segmento` (nombre del catalog).
 *
 * Este helper replica la MISMA semántica que arma `DataTable.tsx` (PUBS): core lógico→físico +
 * native con `mergeWithCatalog`, ids lógicos + `displayName` como header + `accessorFn` de catalog.
 * Devuelve descriptores compatibles con `exportToExcel.ts` (lee `id`, `getIsVisible()`,
 * `columnDef.{header, accessorFn|accessorKey}`), que formatea por id lógico / regex de header.
 *
 * Follow-up (convergencia 100%): que `DataTable.handleExport` también consuma este helper
 * (hoy pasa las columnas vivas de TanStack). Documentado en el issue PT-#61.
 */

export interface SchemaField {
  Field: string;
  [k: string]: any;
}

/**
 * Spec SERIALIZABLE de una columna para el export streaming server-side
 * (POST /api/export/multi-toma-stream). El server no re-deriva columnas del columnConfig:
 * ejecuta este spec (single-source de la semántica de columnas sigue acá). Espeja el `kind`
 * que ya conoce cada rama del builder (physical/catalog/apariciones/fecha).
 */
export interface ExportColumnStreamSpec {
  kind: 'physical' | 'catalog' | 'apariciones' | 'fecha' | 'semana' | 'toma' | 'idpulpou' | 'url';
  field?: string;
  format?: 'currency' | null;
}

export interface ExportColumnDescriptor {
  id: string;
  getIsVisible: () => boolean;
  columnDef: {
    header: string;
    accessorFn?: (row: any) => any;
    accessorKey?: string;
  };
  /** Spec para el export streaming server-side (ver ExportColumnStreamSpec). */
  stream?: ExportColumnStreamSpec;
}

// Ids/campos que se exportan como moneda (espejo de PRICE_COL_IDS/REGEX en exportToExcel.ts).
const PRICE_STREAM_IDS = new Set([
  'sale_price', 'original_price', 'discounted_price',
  'precio', 'precio_desc', 'precio_venta', 'precio_cuota', 'p_cuota', 'pu', 'pum',
]);
const PRICE_STREAM_REGEX = /precio|cuota|^pu$|^pum$/i;
function currencyFormat(id: string, phys?: string): 'currency' | null {
  if (PRICE_STREAM_IDS.has(id) || (phys && PRICE_STREAM_IDS.has(phys))) return 'currency';
  if (PRICE_STREAM_REGEX.test(id) || (phys && PRICE_STREAM_REGEX.test(phys))) return 'currency';
  return null;
}

// Campos que viven en el JOIN a `catalog` — nunca como columna física de la toma.
// (mismo set que CATALOG_ONLY_MERGE_FIELDS en DataTable.tsx)
const CATALOG_ONLY_MERGE_FIELDS = ['catalog_brand', 'catalog_category', 'subcategory', 'segmento', 'contenido', 'udm', 'sku_origen', 'sku_unificado', 'seller'];

// core lógico → candidatos de campo físico (espejo del switch de variantes en DataTable.tsx).
const CORE_PHYSICAL_CANDIDATES: Record<string, string[]> = {
  precio: ['precio', 'original_price', 'price'],
  precio_desc: ['precio_desc', 'discounted_price', 'precio_descuento'],
  precio_venta: ['precio_venta', 'sale_price', 'precioventa'],
  cuotas: ['cuotas', 'installments', 'installements'],
  precio_cuota: ['precio_cuota', 'installements_value_c', 'installements_value', 'valor_cuota'],
  marca: ['marca'],
  titulo: ['titulo', 'title'],
  imagen: ['imagen', 'image'],
  categoria: ['categoria', 'category', 'cat'],
  sitio: ['sitio', 'site'],
  seller: ['seller', 'vendedor'],
  buybox: ['buybox'],
};

// core ids que NO son columnas de datos (acciones/checkbox).
const CORE_NON_DATA = new Set(['actions', 'selection']);

// Nunca exportar como columna (defensivo; además no viven en core/native). `apariciones`
// SÍ se consume para derivar `pagina_orden`, pero no se emite como columna propia (ítem 5).
const NEVER_EXPORT = new Set(['apariciones', 'tags']);

/** Resuelve el nombre físico real (case-insensitive) de la 1ª variante que exista en el schema. */
function resolvePhysical(candidates: string[], schema: SchemaField[]): string | null {
  const fields = schema.map(f => ({ orig: f.Field, low: String(f.Field).toLowerCase() }));
  for (const cand of candidates) {
    const c = cand.toLowerCase();
    const hit = fields.find(f => f.low === c);
    if (hit) return hit.orig;
  }
  for (const cand of candidates) {
    const c = cand.toLowerCase();
    const hit = fields.find(f => f.low.includes(c));
    if (hit) return hit.orig;
  }
  return null;
}

function findAparicionesField(schema: SchemaField[]): string | null {
  const hit = schema.find(f => String(f.Field).toLowerCase() === 'apariciones');
  return hit ? hit.Field : null;
}

/** accessorFn de `pagina_orden`: parsea `apariciones` JSON → {pagina, orden_en_pag} de la 1ª posición.
 *  (mismo algoritmo que DataTable.tsx:2082). exportToExcel espera este shape. */
function paginaOrdenAccessor(aparField: string | null) {
  return (row: any) => {
    const raw = aparField ? row[aparField] : (row?.apariciones ?? row?.Apariciones);
    let apar: any[] = [];
    if (raw) {
      try {
        apar = typeof raw === 'string' ? JSON.parse(raw) : raw;
      } catch {
        apar = [];
      }
      if (!Array.isArray(apar)) apar = [];
    }
    if (apar.length > 0) {
      const sorted = [...apar].sort((a, b) => {
        const pa = parseInt(a.page) || 999;
        const pb = parseInt(b.page) || 999;
        if (pa !== pb) return pa - pb;
        return (parseInt(a.orderInPage) || 999) - (parseInt(b.orderInPage) || 999);
      });
      return { pagina: sorted[0].page || 0, orden_en_pag: sorted[0].orderInPage || 0 };
    }
    return { pagina: 0, orden_en_pag: 0 };
  };
}

/** accessorFn catalog-authoritative: devuelve el valor del catalog (null si vacío) — NO hace
 *  fallback a la toma. Consistente con PUBS (MergedTomaCatalogCell catalogAuthoritative=true). */
function catalogAccessor(catalogField: string) {
  return (row: any) => {
    const v = row?.[catalogField];
    if (v != null && String(v).trim() !== '') return v;
    return null;
  };
}

/**
 * Deriva las columnas de export (mismo criterio que PUBS) desde el `columnConfig` del cliente.
 * @param schema  filas del DESCRIBE (`res.data.schema`) — para resolver core lógico→físico.
 * @param columnConfig  `res.data.columnConfig` (core + native).
 */
export function buildExportColumns(schema: SchemaField[], columnConfig: any): ExportColumnDescriptor[] {
  const safeSchema = Array.isArray(schema) ? schema : [];
  const cc = columnConfig || {};
  const core = cc.core || {};
  const native = cc.native || {};
  const aparField = findAparicionesField(safeSchema);

  const cols: ExportColumnDescriptor[] = [];

  // ── CORE: ordenado por `order`, enabled (default true si no lo dice) ─────────
  const coreEntries = Object.entries(core)
    .filter(([id, e]: any) => {
      if (CORE_NON_DATA.has(id)) return false;
      if (NEVER_EXPORT.has(String(id).toLowerCase())) return false;
      if (!e || typeof e !== 'object') return false;
      if (e.enabled === false) return false;
      // entradas "solo filterable" (ej. subcategory/segmento en core = {filterable:true})
      // no son columnas de display → skip.
      if (e.enabled === undefined && e.order === undefined && e.displayName === undefined) return false;
      return true;
    })
    .sort(([, a]: any, [, b]: any) => (a.order ?? 9999) - (b.order ?? 9999));

  for (const [id, entryRaw] of coreEntries) {
    const entry: any = entryRaw;
    const header = entry.displayName || entry.label || id;

    // merge con catalog (marca→catalog_brand, categoria→catalog_category)
    if (entry.mergeWithCatalog && CATALOG_ONLY_MERGE_FIELDS.includes(String(entry.mergeWithCatalog))) {
      cols.push({ id, getIsVisible: () => true, columnDef: { header, accessorFn: catalogAccessor(String(entry.mergeWithCatalog)) }, stream: { kind: 'catalog', field: String(entry.mergeWithCatalog) } });
      continue;
    }
    if (id === 'pagina_orden') {
      cols.push({ id, getIsVisible: () => true, columnDef: { header, accessorFn: paginaOrdenAccessor(aparField) }, stream: { kind: 'apariciones', field: aparField || 'apariciones' } });
      continue;
    }
    if (id === 'fecha') {
      // exportToExcel special-casea colId 'fecha' (lee item.fecha directo y formatea DD/MM/YYYY).
      cols.push({ id, getIsVisible: () => true, columnDef: { header, accessorKey: 'fecha' }, stream: { kind: 'fecha' } });
      // PT-#63: columna SEMANA (ISO 8601, `AAAA-Www`) pegada a Fecha, derivada del MISMO
      // item.fecha vía getSemanaIso. exportToExcel special-casea colId 'semana'.
      cols.push({ id: 'semana', getIsVisible: () => true, columnDef: { header: 'Semana', accessorKey: 'fecha' }, stream: { kind: 'semana' } });
      continue;
    }
    // columna física normal (precio→original_price, precio_venta→sale_price, etc.)
    const phys = resolvePhysical(CORE_PHYSICAL_CANDIDATES[id] || [id], safeSchema);
    if (!phys) continue;
    cols.push({ id, getIsVisible: () => true, columnDef: { header, accessorKey: phys }, stream: { kind: 'physical', field: phys, format: currencyFormat(id, phys) } });
  }

  // ── NATIVE: aditivo, ordenado por `order`, solo enabled === true ─────────────
  const nativeEntries = Object.entries(native)
    .filter(([id, e]: any) => e && e.enabled === true && !NEVER_EXPORT.has(String(id).toLowerCase()))
    .sort(([, a]: any, [, b]: any) => (a.order ?? 9999) - (b.order ?? 9999));

  for (const [id, entryRaw] of nativeEntries) {
    const entry: any = entryRaw;
    const sourceField = entry.sourceField || id;
    const header = entry.displayName || id;
    const isCatalogOnly = !!entry.mergeWithCatalog && CATALOG_ONLY_MERGE_FIELDS.includes(String(entry.mergeWithCatalog));

    // Native con merge a catalog (segment→segmento, subcategory→subcategory, udm, contenido).
    if (entry.mergeWithCatalog && isCatalogOnly) {
      cols.push({ id, getIsVisible: () => true, columnDef: { header, accessorFn: catalogAccessor(String(entry.mergeWithCatalog)) }, stream: { kind: 'catalog', field: String(entry.mergeWithCatalog) } });
      continue;
    }
    // Native físico (ej. discount). Debe existir en el schema.
    const phys = resolvePhysical([sourceField], safeSchema);
    if (!phys) continue;
    cols.push({ id, getIsVisible: () => true, columnDef: { header, accessorKey: phys }, stream: { kind: 'physical', field: phys, format: currencyFormat(id, phys) } });
  }

  // dedupe por id (core gana sobre native ante colisión).
  const seen = new Set<string>();
  return cols.filter(c => {
    if (seen.has(c.id)) return false;
    seen.add(c.id);
    return true;
  });
}

/**
 * PT-#67: modela las calculadas INYECTADAS del DATA EXPORT (toma, id_pulpou, url) como
 * columnas del template, para que el editor las muestre/ordene/togglee y el export las
 * consuma DESDE el template (sin `pushResolved` hardcodeado en ExportPanel).
 * `semana` NO se agrega acá: ya la produce buildExportColumns pegada a `fecha`.
 * El orden acá es irrelevante — ExportPanel reordena por template / DATA_EXPORT_BASE_ORDER.
 */
export function injectDataExportCalculadas(cols: ExportColumnDescriptor[]): ExportColumnDescriptor[] {
  const ids = new Set(cols.map(c => c.id));
  const calc: ExportColumnDescriptor[] = [];
  if (!ids.has('toma')) {
    calc.push({ id: 'toma', getIsVisible: () => true, columnDef: { header: 'Toma' }, stream: { kind: 'toma' } });
  }
  if (!ids.has('id_pulpou')) {
    calc.push({ id: 'id_pulpou', getIsVisible: () => true, columnDef: { header: 'ID Pulpou' }, stream: { kind: 'idpulpou' } });
  }
  if (!ids.has('url')) {
    calc.push({ id: 'url', getIsVisible: () => true, columnDef: { header: 'URL' }, stream: { kind: 'url' } });
  }
  return [...cols, ...calc];
}
